import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Gap Analysis - Diagnostic tool
 * Identifies gaps in authority positioning and points users to the right tools
 */
function GapAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [visibilityData, setVisibilityData] = useState(null);

  // IP counts for the sidebar
  const [ipCounts, setIpCounts] = useState({
    frameworks: 0,
    processes: 0,
    caseStudies: 0,
    contentPieces: 0,
    bookProjects: 0,
    speakingTopics: 0
  });

  useEffect(() => {
    loadData();
    loadIpCounts();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/authority-engineer/audit/complete', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAuditData(data.audit);
          setVisibilityData(data.visibility);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIpCounts = async () => {
    try {
      const response = await fetch('/api/authority-engineer/dashboard', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIpCounts({
            frameworks: data.ipLibrary?.frameworks || 0,
            processes: data.ipLibrary?.processes || 0,
            caseStudies: data.ipLibrary?.caseStudies || 0,
            contentPieces: 0, // Will add if endpoint available
            bookProjects: 0,
            speakingTopics: 0
          });
        }
      }
    } catch (err) {
      console.error('Error loading IP counts:', err);
    }
  };

  const generateGapAnalysis = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/generate-gaps', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        loadData(); // Refresh to get new analysis
      } else {
        setError(data.error || 'Failed to generate analysis');
      }
    } catch (err) {
      setError('Failed to generate gap analysis');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading analysis...</p>
        </div>
      </Container>
    );
  }

  const hasInventory = auditData && auditData.inventory && Object.keys(auditData.inventory).length > 0;
  const hasAnalysis = auditData && auditData.gapAnalysis;

  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          ← Back to Authority Engineer
        </Link>
        <h1>Gap Analysis</h1>
        <p className="text-muted">Identify gaps in your authority positioning and discover what to build next</p>
      </div>

      <Row>
        <Col lg={8}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          {/* No Inventory Warning */}
          {!hasInventory && (
            <Alert variant="warning">
              <strong>No IP inventory found.</strong> Please complete your IP inventory first to generate a gap analysis.
              <div className="mt-2">
                <Link to="/command-center/authority-engineer/inventory" className="btn btn-warning btn-sm">
                  Go to IP Inventory →
                </Link>
              </div>
            </Alert>
          )}

          {/* Generate Analysis Card */}
          {hasInventory && !hasAnalysis && (
            <Card className="shadow mb-4">
              <Card.Body className="text-center py-5">
                <h4>Ready to Generate Your Gap Analysis</h4>
                <p className="text-muted mb-4">
                  Our AI will analyze your inventory and identify gaps and opportunities in your authority positioning.
                </p>
                <Button
                  size="lg"
                  style={{ backgroundColor: colors.goldPrimary, border: 'none', color: colors.greyDarkest }}
                  onClick={generateGapAnalysis}
                  disabled={generating}
                >
                  {generating ? (
                    <><Spinner size="sm" className="me-2" />Analyzing...</>
                  ) : (
                    'Generate Gap Analysis'
                  )}
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Gap Analysis Results */}
          {hasAnalysis && (
            <>
              {/* Undocumented Expertise */}
              {auditData.undocumented && auditData.undocumented.length > 0 && (
                <Card className="shadow mb-4">
                  <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                    <h5 className="mb-0">Undocumented Expertise</h5>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info" className="mb-3">
                      <strong>A note on this analysis:</strong> The AI may not have complete context about your business.
                      Use these suggestions as prompts for reflection - consider whether each item represents a genuine gap
                      in your documented IP, or something you should address on your website, sales pages, or marketing materials.
                    </Alert>
                    <p className="text-muted mb-3">
                      Based on your profile, you likely have expertise in these areas that hasn't been formally documented:
                    </p>
                    <ul className="mb-4" style={{ paddingLeft: '1.25rem' }}>
                      {auditData.undocumented.map((item, idx) => (
                        <li key={idx} className="mb-2" style={{ color: '#856404' }}>
                          <span style={{ color: '#333' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="dark"
                        style={{ backgroundColor: colors.greyDarkest }}
                        onClick={() => navigate('/command-center/authority-engineer/extract')}
                      >
                        Document in Mind to Method →
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/command-center/authority-engineer/processes')}
                      >
                        Build a Process
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Gap Analysis */}
              <Card className="shadow mb-4">
                <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                  <h5 className="mb-0">Gap Analysis</h5>
                </Card.Header>
                <Card.Body>
                  <Alert variant="light" className="mb-3 border">
                    <small className="text-muted">
                      <strong>How to use this:</strong> Review the gaps identified below. Some may point to missing IP
                      that should be documented. Others may reveal messaging gaps on your website or sales materials.
                      Consider each one and decide the best way to address it.
                    </small>
                  </Alert>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>
                    {auditData.gapAnalysis}
                  </div>
                </Card.Body>
              </Card>

              <div className="text-center">
                <Button
                  variant="outline-dark"
                  onClick={generateGapAnalysis}
                  disabled={generating}
                >
                  {generating ? 'Regenerating...' : 'Regenerate Analysis'}
                </Button>
              </div>
            </>
          )}
        </Col>

        {/* Sidebar - Tools to Fill Gaps */}
        <Col lg={4}>
          {/* Visibility Score */}
          {visibilityData && (
            <Card className="shadow mb-4">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h6 className="mb-0">Your Visibility Score</h6>
              </Card.Header>
              <Card.Body className="text-center">
                <div className="display-4 fw-bold" style={{ color: visibilityData.overallScore >= 50 ? '#28a745' : '#ffc107' }}>
                  {visibilityData.overallScore}/100
                </div>
                <p className="text-muted mb-0">{visibilityData.summary}</p>
                <Link to="/command-center/authority-engineer/visibility" className="btn btn-outline-dark btn-sm mt-3">
                  Run New Check
                </Link>
              </Card.Body>
            </Card>
          )}

          {/* Your IP Status */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Your IP Library</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="border-bottom p-3 d-flex justify-content-between align-items-center">
                <span>Frameworks</span>
                <div>
                  <Badge bg={ipCounts.frameworks > 0 ? 'success' : 'secondary'} className="me-2">
                    {ipCounts.frameworks}
                  </Badge>
                  {ipCounts.frameworks === 0 && (
                    <Link to="/command-center/authority-engineer/extract" className="small">Add →</Link>
                  )}
                </div>
              </div>
              <div className="border-bottom p-3 d-flex justify-content-between align-items-center">
                <span>Processes</span>
                <div>
                  <Badge bg={ipCounts.processes > 0 ? 'success' : 'secondary'} className="me-2">
                    {ipCounts.processes}
                  </Badge>
                  {ipCounts.processes === 0 && (
                    <Link to="/command-center/authority-engineer/processes" className="small">Add →</Link>
                  )}
                </div>
              </div>
              <div className="border-bottom p-3 d-flex justify-content-between align-items-center">
                <span>Case Studies</span>
                <div>
                  <Badge bg={ipCounts.caseStudies > 0 ? 'success' : 'secondary'} className="me-2">
                    {ipCounts.caseStudies}
                  </Badge>
                  {ipCounts.caseStudies === 0 && (
                    <Link to="/command-center/case-study" className="small">Add →</Link>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tools to Fill Gaps */}
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Tools to Fill Gaps</h6>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Use these tools to document and leverage your expertise:
              </p>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/extract')}
                >
                  Mind to Method (Extract IP)
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/processes')}
                >
                  Process Builder
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/case-study')}
                >
                  Case Study Writer
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/content')}
                >
                  Content Matrix
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/book')}
                >
                  Book Builder
                </Button>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => navigate('/command-center/authority-engineer/speaking')}
                >
                  Speaking Topics
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default GapAnalysis;
